import { useEffect } from 'react'
import { useForm, FieldValues } from 'react-hook-form'

import { prepareExpirationDate } from '@/utils/helpers'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/newRedesign/primitives/Dialog'
import { Button } from '@/components/newRedesign/primitives/Button'
import { Form } from '@/components/ui/form'
import {
  FormInput,
  FormSelect,
  FormDatePicker,
  FormSearchableSelect,
  FormTextarea,
} from '@/components/newRedesign/form'
import { SaveIcon } from 'lucide-react'

import {
  useCurrencies,
  useSuppliersFetcher,
} from '../hooks/useContainerQueries'

export interface ProductFormData {
  name: string
  type: string
  salePrice: {
    amount: number | undefined
    codeCurrency: string
  }
  expirationAt: string | Date
  entryQuantity: number
  noPackages?: number
  supplierId: number
  registeredPrice: {
    amount: number | undefined
    codeCurrency: string
  }
  description?: string
}

interface ProductDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: ProductFormData) => void
  onCancel: () => void
  isPending?: boolean
}

export function ProductDialog({
  open,
  onOpenChange,
  onSave,
  isPending = false,
}: ProductDialogProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      name: '',
      type: '',
      salePrice: {
        amount: undefined,
        codeCurrency: '',
      },
      expirationAt: '',
      entryQuantity: undefined,
      noPackages: undefined,
      supplierId: undefined,
      registeredPrice: {
        amount: undefined,
        codeCurrency: '',
      },
      description: '',
    },
  })

  // Usar hooks con cache de React Query
  const fetchSuppliers = useSuppliersFetcher()
  const { data: currencies } = useCurrencies()

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset({
        name: '',
        type: '',
        salePrice: {
          amount: undefined,
          codeCurrency: '',
        },
        expirationAt: '',
        entryQuantity: undefined,
        noPackages: undefined,
        supplierId: undefined,
        registeredPrice: {
          amount: undefined,
          codeCurrency: '',
        },
        description: '',
      })
    }
  }, [open, form])

  const handleSubmit = (data: FieldValues) => {
    // Prepare expiration date using helper (sets time to 04:00)
    const expirationAt = prepareExpirationDate(data.expirationAt)

    onSave({
      ...data,
      expirationAt: expirationAt || '',
    } as ProductFormData)
  }

  const productTypes = [
    { value: 'STOCK', label: 'Almacén ' },
    // { value: 'RAW', label: 'Materia Prima' },
    // { value: 'MANUFACTURED', label: 'Manufacturado' },
    // { value: 'INPUT', label: 'Insumo' },
    // { value: 'ASSET', label: 'Activo' },
    // { value: 'VARIATION', label: 'Variación' },
  ]

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={
          'flex h-auto max-h-[100dvh] w-full max-w-[100vw] flex-col overflow-hidden ' +
          'rounded-lg bg-[#FAFAFA] p-0 shadow-lg sm:max-h-[min(797px,90vh)] sm:w-[min(800px,95vw)] sm:max-w-[800px]'
        }
      >
        {/* Dialog Header */}
        <DialogHeader
          className={
            'flex h-[52px] shrink-0 flex-row items-center ' +
            'justify-between border-b border-[#D4D4D4] px-3 py-3 sm:p-4'
          }
        >
          <DialogTitle
            className={
              'text-lg font-semibold leading-tight tracking-[-0.02em] text-[#262626] ' +
              'sm:text-xl sm:leading-[24px]'
            }
          >
            Nuevo Producto
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div
            className={
              'flex max-h-[calc(100dvh-104px)] min-h-0 flex-1 flex-col gap-3 overflow-y-auto ' +
              'bg-[#FAFAFA] p-3 sm:max-h-[calc(min(797px,90vh)-120px)] sm:gap-4 sm:p-4'
            }
          >
            {/* Type - Full Width */}
            <FormSelect
              control={form.control}
              data={productTypes.map((p) => ({
                id: p.value,
                title: p.label,
              }))}
              label="Tipo"
              name="type"
              placeholder="Seleccionar"
              rules={{
                required: 'El tipo es requerido',
              }}
              showLabel
            />

            {/* Name - Full Width */}
            <FormInput
              control={form.control}
              inputSize="md"
              label="Nombre"
              name="name"
              placeholder="Nombre"
              rules={{
                required: 'El nombre es requerido',
              }}
              showLabel
              type="text"
            />

            {/* Sale Price and Currency - Same Row 50/50 */}
            <div className="flex flex-col gap-2">
              <label
                className={
                  'text-sm font-medium leading-5 ' +
                  'tracking-[0.005em] text-[#262626]'
                }
              >
                Precio de venta <span className="text-[#262626]">(*)</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <FormInput
                  control={form.control}
                  inputSize="md"
                  name="salePrice.amount"
                  placeholder="0.00"
                  rules={{
                    required: 'El precio de venta es requerido',
                  }}
                  showLabel={false}
                  type="number"
                />
                <FormSelect
                  control={form.control}
                  data={
                    currencies?.map(({ code }) => ({
                      id: code,
                      title: code,
                    })) || []
                  }
                  name="salePrice.codeCurrency"
                  placeholder="Moneda"
                  rules={{
                    required: 'La moneda es requerida',
                  }}
                  showLabel={false}
                />
              </div>
            </div>

            {/* Separator that doesn't reach the end */}
            <div className="h-px bg-[#D4D4D4]" />

            {/* Add Batch Title */}
            <div className="text-base font-medium text-[#262626]">
              Agregar Lote
            </div>

            {/* Expiration Date */}
            <FormDatePicker
              control={form.control}
              label="Fecha de expiración"
              minDate={new Date()}
              name="expirationAt"
              placeholder="Fecha"
              rules={{
                required: 'La fecha de expiración es requerida',
              }}
              showLabel
            />

            {/* Quantity and Package Number Row */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              <FormInput
                control={form.control}
                inputSize="md"
                label="Cantidad"
                name="entryQuantity"
                placeholder="Cantidad"
                rules={{
                  required: 'La cantidad es requerida',
                  validate: (value: number) =>
                    value > 0 || 'La cantidad debe ser mayor a 0',
                }}
                showLabel
                type="number"
              />

              <FormInput
                control={form.control}
                inputSize="md"
                label="Número de paquetes"
                name="noPackages"
                placeholder="Número de paquetes"
                showLabel
                type="number"
              />
            </div>

            {/* Supplier and Registered Price Row */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              {/* Supplier - ocupa 1 de 2 columnas (376px aprox) */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Proveedor <span className="text-[#262626]">(*)</span>
                </label>
                <FormSearchableSelect
                  control={form.control}
                  dropdownPosition="top"
                  emptyMessage="No se encontraron proveedores"
                  fetcher={fetchSuppliers}
                  name="supplierId"
                  placeholder="Seleccionar"
                  rules={{
                    required: 'El proveedor es requerido',
                  }}
                  searchPlaceholder="Buscar proveedor..."
                  showLabel={false}
                />
              </div>

              {/* Registered Price + Currency - ocupa 1 de 2 columnas (376px aprox) */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Precio <span className="text-[#262626]">(*)</span>
                </label>
                <div className="flex gap-2">
                  {/* Price Input - flexible width */}
                  <div className="flex-1">
                    <FormInput
                      control={form.control}
                      inputSize="md"
                      name="registeredPrice.amount"
                      placeholder="0.00"
                      rules={{
                        required: 'El precio es requerido',
                        validate: (value: number) => {
                          if (value <= 0) {
                            return 'El precio debe ser mayor a 0'
                          }
                          return true
                        },
                      }}
                      showLabel={false}
                      type="number"
                    />
                  </div>
                  {/* Currency Select - 120px fixed */}
                  <div className="w-[100px] sm:w-[120px]">
                    <FormSelect
                      control={form.control}
                      data={
                        currencies?.map(({ code }) => ({
                          id: code,
                          title: code,
                        })) || []
                      }
                      name="registeredPrice.codeCurrency"
                      placeholder="Moneda"
                      rules={{
                        required: 'La moneda es requerida',
                      }}
                      showLabel={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Observations */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Observaciones
              </label>
              <FormTextarea
                control={form.control}
                name="description"
                placeholder="Observaciones"
                rows={3}
                showLabel={false}
              />
            </div>
          </div>

          {/* Dialog Footer */}
          <DialogFooter
            className={
              'flex h-auto shrink-0 items-center justify-end ' +
              'border-t border-[#D4D4D4] p-3 sm:h-[68px] sm:p-4'
            }
          >
            <Button
              className={
                'flex h-9 w-full items-center justify-center gap-2 rounded-md bg-[#1E293B] px-4 hover:bg-[#334155] ' +
                'sm:h-9 sm:w-auto sm:min-w-[110px]'
              }
              disabled={isPending}
              icon={<SaveIcon className="h-4 w-4" />}
              onClick={form.handleSubmit(handleSubmit)}
              type="button"
              variant="default"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
